// Fiche d'un événement (M21, lot L5-C1, H7-05, D-253) : organisateur, dates, lieu, budget prévu et dépenses
// engagées (lues dans la chaîne de la dépense), réservation (état et paiement du module des équipements),
// autorisation d'occupation, alertes de cohérence, publication par le secrétaire général, annulation, bilan.
// Un lien n'est détaillé qu'avec le droit de son domaine.
import { CATEGORIES_EVENEMENT, ORGANISATEURS_EVENEMENT } from '@cc/contrats';
import { Bandeau, Bouton, Lien, PuceEtat } from '@cc/ui';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { INVALIDER_EVENEMENTS, publierEvenement, retirerDepense, useEvenement } from './api';
import { FormulaireAnnulation, FormulaireAutorisation, FormulaireBilan, FormulaireDepense, FormulaireEvenement, FormulaireReservation } from './FormulairesEvenements';
import { libelleEvenement, montantFcfa, TON_EVENEMENT } from './logique';
import styles from '../achats/achats.module.css';

type Dialogue = 'modifier' | 'reservation' | 'autorisation' | 'depense' | 'annulation' | 'bilan' | null;
const RESERVE = 'Détail réservé aux agents habilités dans ce domaine.';

export function PageEvenement() {
  const { id } = useParams();
  const { contexte } = useSession();
  const evenement = useEvenement(id);
  const [d, setD] = useState<Dialogue>(null);
  const publier = useAction({ executer: () => publierEvenement(id as string), succes: 'Événement publié au calendrier du portail.', invalider: INVALIDER_EVENEMENTS });
  const retirer = useAction({ executer: (g: string) => retirerDepense(id as string, g), succes: 'Engagement retiré de l\'événement.', invalider: INVALIDER_EVENEMENTS });
  const e = evenement.data;
  const modifier = peut(contexte, 'evenement', 'modifier');
  const actif = e ? e.etat === 'en_preparation' || e.etat === 'publie' : false;
  const termine = e ? new Date(e.fin).getTime() <= Date.now() : false;
  const fermer = () => setD(null);
  return (
    <>
      <EnTetePage etiquette="Événements" titre={e ? `${e.numero} : ${e.intitule}` : 'Événement'} description={e ? `${CATEGORIES_EVENEMENT[e.categorie]}, du ${dateHeure(e.debut)} au ${dateHeure(e.fin)}, ${e.lieu}.` : undefined} actions={<Lien href="/evenements">Calendrier</Lien>} />
      <EtatRequete chargement={evenement.isPending} erreur={evenement.error} onReessayer={() => void evenement.refetch()} lot="L5-C1" fonction="La fiche de l'événement">
        {e ? (
          <>
            <div className={styles.actes}>
              <PuceEtat ton={TON_EVENEMENT[e.etat]} libelle={libelleEvenement(e.etat)} />
              {modifier && e.etat === 'en_preparation' ? <Bouton variante="secondaire" onClick={() => setD('modifier')}>Modifier</Bouton> : null}
              {peut(contexte, 'evenement', 'valider') && e.etat === 'en_preparation' ? <Bouton variante="principal" chargement={publier.enCours} onClick={() => publier.lancer(undefined)}>Publier</Bouton> : null}
              {modifier && actif && termine ? <Bouton variante="principal" onClick={() => setD('bilan')}>Saisir le bilan</Bouton> : null}
              {modifier && actif ? <Bouton variante="danger" onClick={() => setD('annulation')}>Annuler l&apos;événement</Bouton> : null}
            </div>
            {publier.messageErreur ? <Bandeau type="erreur">{publier.messageErreur}</Bandeau> : null}
            {e.alertes.map((a) => <Bandeau key={a} type="attention">{a}</Bandeau>)}
            <section className={styles.bloc} aria-labelledby="titre-organisation">
              <h2 id="titre-organisation">Organisation</h2>
              <p>{`${ORGANISATEURS_EVENEMENT[e.organisateur_type]} : ${e.organisateur}${e.organisateur_contact ? ` (${e.organisateur_contact})` : ''}`}</p>
              {e.description ? <p className={styles.detail}>{e.description}</p> : null}
              <p>{`Texte public : ${e.texte_public ?? 'non rédigé (exigé pour la publication)'}`}</p>
              {e.publie_le ? <p className={styles.detail}>{`Publié le ${dateHeure(e.publie_le)}${e.publie_par ? ` par ${e.publie_par}` : ''}. La publication ne vaut pas autorisation d'occuper un espace.`}</p> : null}
              {e.motif_annulation ? <p className={styles.detail}>{`Annulé : ${e.motif_annulation}`}</p> : null}
            </section>
            <section className={styles.bloc} aria-labelledby="titre-espace">
              <h2 id="titre-espace">Espace et autorisation</h2>
              <h3>Réservation d&apos;équipement</h3>
              {e.reservation ? <p>{e.reservation.visible ? `${e.reservation.numero} : ${e.reservation.equipement}, le ${date(e.reservation.date)} de ${e.reservation.heure_debut} à ${e.reservation.heure_fin}, ${e.reservation.etat_libelle}${e.reservation.paiement ? ` · ${e.reservation.paiement}` : ''}` : RESERVE}</p> : <p className={styles.detail}>Aucune réservation liée.</p>}
              {modifier && actif ? <Bouton variante="secondaire" onClick={() => setD('reservation')}>{e.reservation ? 'Demander une autre réservation' : 'Réserver un équipement'}</Bouton> : null}
              <h3>Autorisation d&apos;occupation du domaine public</h3>
              {e.autorisation ? <p>{e.autorisation.visible ? `${e.autorisation.numero} : ${e.autorisation.categorie}, du ${date(e.autorisation.debut)} au ${date(e.autorisation.fin)} (${e.autorisation.etat})` : RESERVE}</p> : <p className={styles.detail}>Aucune autorisation liée.</p>}
              {modifier && actif ? <Bouton variante="secondaire" onClick={() => setD('autorisation')}>Rattacher une autorisation</Bouton> : null}
            </section>
            <section className={styles.bloc} aria-labelledby="titre-budget">
              <h2 id="titre-budget">Budget et dépenses</h2>
              <p>{`Budget prévu : ${montantFcfa(e.budget_prevu, e.fictif)} · dépenses engagées : ${e.depenses_engagees === null ? RESERVE : montantFcfa(e.depenses_engagees, e.fictif)}`}</p>
              {e.depenses.length === 0 ? <p className={styles.detail}>Aucun engagement rattaché.</p> : (
                <ul>{e.depenses.map((g) => (
                  <li key={g.engagement_id}>
                    {g.visible ? `${g.numero ?? 'Sans numéro'} : ${g.tiers}, ${montantFcfa(g.montant, e.fictif)} (${g.etat})` : RESERVE}
                    {modifier && actif && g.visible ? <Bouton variante="discret" chargement={retirer.enCours} onClick={() => retirer.lancer(g.engagement_id)}>Retirer</Bouton> : null}
                  </li>
                ))}</ul>
              )}
              {modifier && actif ? <Bouton variante="secondaire" onClick={() => setD('depense')}>Rattacher un engagement</Bouton> : null}
            </section>
            {e.etat === 'tenu' ? (
              <section className={styles.bloc} aria-labelledby="titre-bilan">
                <h2 id="titre-bilan">Bilan</h2>
                <p>{`Fréquentation : ${e.frequentation ?? 0} personnes${e.fictif ? ' (fictif)' : ''}`}</p>
                <p>{e.bilan}</p>
              </section>
            ) : null}
          </>
        ) : null}
      </EtatRequete>
      {e && d === 'modifier' ? <FormulaireEvenement fiche={e} onFermer={fermer} /> : null}
      {e && d === 'reservation' ? <FormulaireReservation id={e.id} onFermer={fermer} /> : null}
      {e && d === 'autorisation' ? <FormulaireAutorisation id={e.id} onFermer={fermer} /> : null}
      {e && d === 'depense' ? <FormulaireDepense id={e.id} onFermer={fermer} /> : null}
      {e && d === 'annulation' ? <FormulaireAnnulation id={e.id} onFermer={fermer} /> : null}
      {e && d === 'bilan' ? <FormulaireBilan id={e.id} onFermer={fermer} /> : null}
    </>
  );
}
